<script setup lang="js">

import Fields from "@/components/Fields.vue";
import ItemInformation from "@/components/ItemInformation.vue";
import ItemVisualizationDialog from "@/components/ItemVisualizationDialog.vue";
import SupportImageDialog from "@/components/SupportImageDialog.vue";
import QuestionService from "@/services/question.service.js";
import ErrorUtils from "@/utils/error.utils.js";
import { onBeforeUnmount, onMounted, ref } from "vue";

import Navbar from "@/components/Navbar.vue";
import { useConfirm } from "primevue/useconfirm";

const acceptImageTypes = ref('image/jpg,image/jpeg,image/png,image/gif');
const invalidFileTypeMessage = ref('O tipo de arquivo selecionado não é suportado. Tipos permitidos: JPG, JPEG, PNG e GIF.');
const maxFileSize = ref(10000000);
const invalidFileSizeMessage = ref('O tamanho do arquivo deve ser no máximo 10MB.');
const supportImagesDialogVisible = ref(false);
const selectedModel = ref();

const confirm = useConfirm();

const getDefaultSupportImage = () => {
  return {
    file: null,
    base64: null,
    description: null,
    source: null
  };
}

const selectedSupportImagesFiles = ref([getDefaultSupportImage()]);
const supportImagesErrorMessage = ref('');
const tagsMessage = ref({});
const isGeneratingTags = ref(false);
const isUpdatingItemProgrammaticaly = ref(false);

const itemTabName = ref('Novo item');

const getDefaultItem = () => {
  return {
    id: null,
    information: {
      technologicalArea: null,
      course: null,
      capacity: null,
      subfunction: null,
      knowledge: [],
      difficultyLevel: { value: 2, label: 'Médio' },
      format: {value: 'multiple', label: 'Múltipla escolha'},
      numberOfAlternatives: 5,
      supportImages: [],
      tags: [],
      additionalInstructions: null
    },
    question: {}
  }
};

const item = ref(getDefaultItem());

const itemBeingVisualized = ref({});
const itemVisualizationDialogVisible = ref(false);

const technologicalAreas = ref([]);
const courses = ref({});
const subfunctions = ref({});
const capacities = ref([]);
const knowledge = ref({});

const difficultyLevels = ref([
  {value: 1, label: 'Fácil'},
  {value: 2, label: 'Médio'},
  {value: 3, label: 'Difícil'},
]);

const formats = ref([
  {value: 'multiple', label: 'Múltipla escolha'},
]);

const isDisableKnowledge = () => {
  return !item.value.information.subfunction || !item.value.information.capacity;
}

const onSupportImagesSelect = (event) => {
  try {
    supportImagesErrorMessage.value = '';

    const file = event.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64String = e.target.result;

        const description = selectedSupportImagesFiles.value.length ? selectedSupportImagesFiles.value[0].description : '';
        const source = selectedSupportImagesFiles.value.length ? selectedSupportImagesFiles.value[0].source : '';

        selectedSupportImagesFiles.value[0] = {
          file: file,
          name: file.name,
          type: file.type,
          base64: base64String,
          description: description,
          source: source
        };
      };
      reader.readAsDataURL(file);
    }
  } catch (e) {
    supportImagesErrorMessage.value = 'Ocorreu um erro ao selecionar a imagem de suporte.';
    ErrorUtils.handleError(e);
  }
}

const editSupportImages = () => {
  selectedSupportImagesFiles.value = [item.value.information.supportImages[0]];
  supportImagesDialogVisible.value = true;
}

const updateItem = (newItem) => {
  item.value = newItem;
}

// Função updateItems removida - histórico desabilitado

// Função updateStoredItem removida - histórico desabilitado

const hideSupportImages = () => {
  selectedSupportImagesFiles.value = [getDefaultSupportImage()];
  supportImagesErrorMessage.value = '';
  supportImagesDialogVisible.value = false;
}

const saveSupportImages = () => {
  try {
    if (!selectedSupportImagesFiles.value[0].file) {
      supportImagesErrorMessage.value = 'Selecione uma imagem de suporte.';
      return;
    }

    supportImagesDialogVisible.value = false;
    item.value.information.supportImages = [];
    let image = selectedSupportImagesFiles.value[0];
    image = {...image, order: 1};
    item.value.information.supportImages.push(image);
  } catch (e) {
    supportImagesErrorMessage.value = 'Ocorreu um erro ao salvar a imagem de suporte.';
    ErrorUtils.handleError(e);
  }
}

const removeSupportImage = () => {
  try {
    item.value.information.supportImages = [];
    selectedSupportImagesFiles.value = [getDefaultSupportImage()];
    supportImagesErrorMessage.value = '';
  } catch (e) {
    supportImagesErrorMessage.value = 'Ocorreu um erro ao remover a imagem de suporte.';
    ErrorUtils.handleError(e);
  }
}

const generateTags = () => {
  const errors = [];
  tagsMessage.value = {};

  if (!item.value.information.technologicalArea) {
    errors.push('A área tecnológica é obrigatória.</br>');
  }

  if (!item.value.information.course) {
    errors.push('O curso é obrigatório.</br>');
  }

  if (!item.value.information.capacity) {
    errors.push('A capacidade é obrigatória.</br>');
  }

  if (!item.value.information.subfunction) {
    errors.push('A subfunção é obrigatória.</br>');
  }

  if (item.value.information.knowledge.length <= 0) {
    errors.push('Pelo menos um conhecimento deve ser selecionado.</br>');
  }

  if (errors.length > 0) {
    tagsMessage.value = {message: errors.join(' '), error: true};
    return;
  }

  if (isGeneratingTags.value) {
    return;
  }

  try {
    QuestionService.generateTags(tagsMessage, item, isGeneratingTags, selectedModel.value);
  } catch (error) {
    console.error('[HOME] Erro ao chamar geração de tags:', error);
    tagsMessage.value = {
      message: 'Erro interno. Tente novamente.',
      error: true
    };
  }

  if (isUpdatingItemProgrammaticaly.value) {
    return;
  }

}

// Função editGeneratedItem removida - histórico desabilitado

const openSupportImagesDialog = () => {
  supportImagesDialogVisible.value = true;
}

const onTechnologicalAreaChange = () => {
  const idTechnologicalArea = item.value.information.technologicalArea?.idTechnologicalArea;
  if (idTechnologicalArea) {
    const course = courses[idTechnologicalArea];
    if (!course) {
      QuestionService.getCoursesByTechnologicalArea(idTechnologicalArea)
          .then(response => courses.value[idTechnologicalArea] = response.data)
          .catch(e => ErrorUtils.handleError(e));
    }
  }

  if (isUpdatingItemProgrammaticaly.value) {
    return;
  }
  item.value.information.course = null;
  item.value.information.capacity = null;
  item.value.information.subfunction = null;
  item.value.information.knowledge = [];
}

const onCourseChange = () => {
  if (isUpdatingItemProgrammaticaly.value) {
    return;
  }

  const idCourse = item.value.information.course?.idCourse;
  if (idCourse) {
    QuestionService.getCapacities(idCourse)
        .then(response => capacities.value = response.data)
        .catch(e => ErrorUtils.handleError(e));
  }

  item.value.information.capacity = null;
  item.value.information.subfunction = null;
  item.value.information.knowledge = [];
};

const onCapacityChange = () => {
  const idCapacity = item.value.information.capacity?.idCapacity;
  if (idCapacity) {
    const subfunction = subfunctions[idCapacity];
    if (!subfunction) {
      QuestionService.getSubfunctionsByCapacity(idCapacity)
          .then(response => {
            subfunctions.value[idCapacity] = response.data;
          })
          .catch(e => ErrorUtils.handleError(e));
    }
  }

  if (isUpdatingItemProgrammaticaly.value) {
    return;
  }

  item.value.information.subfunction = null;
  item.value.information.knowledge = [];
};

const onSubfunctionChange = () => {
  const idCapacity = item.value.information.capacity?.idCapacity;
  const idSubfunction = item.value.information.subfunction?.idSubfunction;
  if (idCapacity && idSubfunction) {
    if (!knowledge[idCapacity]) {
      knowledge.value[idCapacity] = {};
    }

    const k = knowledge.value[idCapacity][idSubfunction];
    if (!k) {
      QuestionService.getKnowledgeBySubfunctionCapacity(idSubfunction, idCapacity)
          .then(response => knowledge.value[idCapacity][idSubfunction] = response.data)
          .catch(e => ErrorUtils.handleError(e));
    }
  }

  if (isUpdatingItemProgrammaticaly.value) {
    return;
  }

  item.value.information.knowledge = [];
};

const visualizeItem = (item) => {
  itemBeingVisualized.value = item;
  itemVisualizationDialogVisible.value = true;
}

// Função removeGeneratedItem removida - histórico desabilitado

const hideItemVisualization = () => {
  itemBeingVisualized.value = null;
  itemVisualizationDialogVisible.value = false;
}

// Função getGroupedItems removida - histórico desabilitado

const resetItem = () => {
  item.value = getDefaultItem();
  item.value.information.supportImages = [];
  selectedSupportImagesFiles.value = [getDefaultSupportImage()];
  supportImagesErrorMessage.value = '';
  tagsMessage.value = {};
  itemTabName.value = "Novo item";
}

const handleModelChange = (model) => {
  selectedModel.value = model;
}

onMounted(() => {
  QuestionService.getTechnologicalAreas()
      .then(response => technologicalAreas.value = response.data)
      .catch(e => ErrorUtils.handleError(e));
});

onBeforeUnmount(() => {
  if (QuestionService.value) {
    QuestionService.value.closeTagsSocket();
  }
})
</script>

<template>
  <div class="fade-in-up">
    <Navbar @update:selectedModel="handleModelChange"/>

    <div class="main">
      <div class="container flex direction-column">
        <div class="flex flex-column md:flex-row border-top">
          <div class="fields-section">
            <Fields
                :item="item"
                :technologicalAreas="technologicalAreas"
                :courses="courses"
                :subfunctions="subfunctions"
                :capacities="capacities"
                :knowledge="knowledge"
                :difficultyLevels="difficultyLevels"
                :formats="formats"
                :openSupportImagesDialog="openSupportImagesDialog"
                :onTechnologicalAreaChange="onTechnologicalAreaChange"
                :onCourseChange="onCourseChange"
                :onSubfunctionChange="onSubfunctionChange"
                :onCapacityChange="onCapacityChange"
                :isDisableKnowledge="isDisableKnowledge"
                :generateTags="generateTags"
                :isGeneratingTags="isGeneratingTags"
                :tagsMessage="tagsMessage"
                :resetItem="resetItem"
            />
          </div>

          <div class="content-section w-full">
            <div class="item-information-container">
              <ItemInformation
                  :item="item"
                  :editSupportImages="editSupportImages"
                  :selectedModel="selectedModel"
                  @visualizeItem="visualizeItem"
                  @updateItem="updateItem"
                  @removeImage="removeSupportImage"
              />
            </div>
          </div>
        </div>

        <ItemVisualizationDialog
            :item="itemBeingVisualized"
            :hideItemVisualization="hideItemVisualization"
            :itemVisualizationDialogVisible="itemVisualizationDialogVisible"
        />

        <SupportImageDialog
            :hideSupportImages="hideSupportImages"
            :supportImagesDialogVisible="supportImagesDialogVisible"
            @update:supportImagesDialogVisible="supportImagesDialogVisible = $event"
            :selectedSupportImagesFiles="selectedSupportImagesFiles"
            :acceptImageTypes="acceptImageTypes"
            :invalidFileTypeMessage="invalidFileTypeMessage"
            :maxFileSize="maxFileSize"
            :invalidFileSizeMessage="invalidFileSizeMessage"
            :onSupportImagesSelect="onSupportImagesSelect"
            :saveSupportImages="saveSupportImages"
            :supportImagesErrorMessage="supportImagesErrorMessage"
            @removeImage="removeSupportImage"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.fields-section {
  background: transparent;
  border-radius: 0;
  border-right: none;
  padding: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100% !important;
  min-width: 0 !important;
  max-width: 100% !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

.content-section {
  background: transparent;
  border-radius: 0;
  padding: 1.5rem;
  min-height: 600px;
  flex: 1;
  width: 100% !important;
  min-width: 0 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

.item-information-container {
  height: 100%;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

:deep(.p-tablist-tab-list) {
  border: none !important;
  border-style: none !important;
  gap: 0.5rem !important;
}

.tab-modern {
  border-radius: 4px !important;
  font-weight: 500 !important;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
  
  &[aria-selected="true"] {
    background: rgba(245, 99, 26, 0.15) !important;
    color: #c23a0d !important;
    border: 1px solid rgba(245, 99, 26, 0.3) !important;
  }
  
}

.tab-panels-modern {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(229, 231, 235, 0.6);
  border-top: none;
  border-radius: 0 0 4px 4px;
  min-height: 500px;
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

.tab-panel-modern {
  padding: 1.5rem !important;
  animation: fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

@media (max-width: 768px) {
  .fields-section {
    border-radius: 0;
    border-right: none;
    border-bottom: 1px solid rgba(229, 231, 235, 0.4);
    padding: 1rem;
    width: 100% !important;
    max-width: 100% !important;
  }
  
  .content-section {
    border-radius: 0;
    padding: 1rem;
    min-height: 400px;
    width: 100% !important;
    max-width: 100% !important;
  }
  
  .divider-modern {
    display: none;
  }
  
  .tab-panels-modern {
    min-height: 350px;
  }
  
  .tab-panel-modern {
    padding: 1rem !important;
  }
}

@media (min-width: 769px) {
  .fields-section {
    border-right: 1px solid rgba(229, 231, 235, 0.4);
    flex: 0 0 400px;
    min-width: 400px !important;
    max-width: 400px !important;
    padding: 1.5rem;
    overflow: hidden;
  }
  
  .content-section {
    padding: 1.5rem;
    min-height: 600px;
    flex: 1;
    min-width: 0;
  }
  
  .tab-panels-modern {
    min-height: 500px;
  }
}

@media (min-width: 1024px) {
  .fields-section {
    flex: 0 0 450px;
    min-width: 450px !important;
    max-width: 450px !important;
    padding: 2rem;
  }
  
  .content-section {
    padding: 2rem;
  }
  
  .item-information-container {
    padding: 2rem !important;
  }
}

@media (min-width: 1280px) {
  .fields-section {
    flex: 0 0 500px;
    min-width: 500px !important;
    max-width: 500px !important;
  }
}
</style>
